"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, Cpu, Funnel as Filter, Grid3x3 as Grid3X3, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface CategoryItem {
  id: string;
  title: L6;
  description: L6;
  specs: L6;
  materials: L6;
  link: string;
  badge: L6;
  image: string;
  srcset: string;
  sizes: string;
  iconName: "grid" | "layers" | "filter" | "cpu";
}
const categories: CategoryItem[] = [{
  id: "woven-mesh",
  title: {
    en: "Woven wire mesh",
    zh: "编织丝网",
    es: "Malla de alambre tejida",
    de: "Drahtgewebe",
    fr: "Toile métallique tissée",
    pt: "Malha de arame tecida"
  },
  description: {
    en: "Precision square and Dutch weaves engineered for accurate particle retention, high flow rates, and uniform opening distribution.",
    zh: "精密方孔与密纹荷兰编织网，具备精准的颗粒截留率、高通量和均匀的网孔分布。",
    es: "Tejidos cuadrados y holandeses de precisión para retención de partículas, altos caudales y distribución uniforme de aperturas.",
    de: "Präzisions-Quadrat- und Tressengewebe für exakte Partikelabscheidung, hohe Durchflussraten und gleichmäßige Öffnungsverteilung.",
    fr: "Tissages carrés et hollandais de précision pour rétention particulaire, débits élevés et distribution régulière des ouvertures.",
    pt: "Tramas quadradas e holandesas de precisão para retenção de partículas, altas vazões e distribuição uniforme de aberturas."
  },
  specs: {
    en: "1 to 635 mesh | 1 µm – 25 mm apertures",
    zh: "1 至 635 目 | 1 µm – 25 mm 孔径",
    es: "1 a 635 mesh | Aberturas de 1 µm a 25 mm",
    de: "1 bis 635 Mesh | 1 µm – 25 mm Maschenweite",
    fr: "1 à 635 mesh | Ouvertures de 1 µm à 25 mm",
    pt: "1 a 635 mesh | Aberturas de 1 µm a 25 mm"
  },
  materials: {
    en: "AISI 304, 304L, 316, 316L, 314",
    zh: "AISI 304, 304L, 316, 316L, 314",
    es: "AISI 304, 304L, 316, 316L, 314",
    de: "AISI 304, 304L, 316, 316L, 314",
    fr: "AISI 304, 304L, 316, 316L, 314",
    pt: "AISI 304, 304L, 316, 316L, 314"
  },
  link: "/WovenWireMeshPage",
  badge: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_mesh_catalog.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_mesh_catalog.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_mesh_catalog.png-wvc-sizes",
  iconName: "grid"
}, {
  id: "sintered-mesh",
  title: {
    en: "Sintered wire mesh",
    zh: "烧结丝网",
    es: "Malla de alambre sinterizada",
    de: "Sinterdrahtgewebe",
    fr: "Toile métallique frittée",
    pt: "Malha de arame sinterizada"
  },
  description: {
    en: "Multi-layer diffusion-bonded laminates offering rigid structural integrity, precise micron ratings, and resistance to high backwash pressures.",
    zh: "多层扩散结合层叠烧结网，具备刚性结构强度、精准微米过滤精度以及出色的耐反洗压力性能。",
    es: "Laminados multicapa unidos por difusión con integridad estructural rígida, micrajes precisos y resistencia a retrolavado.",
    de: "Mehrlagige diffusionsgebundene Laminate mit hoher Formstabilität, präzisen Filterfeinheiten und Rückspülbeständigkeit.",
    fr: "Stratifiés multicouches soudés par diffusion offrant rigidité structurelle, seuils précis et résistance au rétrolavage.",
    pt: "Laminados multicamadas unidos por difusão com rigidez estrutural, micragens precisas e resistência a retrolavagem."
  },
  specs: {
    en: "1 to 200 µm ratings | Multi-layer laminates",
    zh: "1 至 200 µm 精度 | 多层复合烧结结构",
    es: "1 a 200 µm | Laminados multicapa",
    de: "1 bis 200 µm Feinheit | Mehrlagige Laminate",
    fr: "1 à 200 µm | Stratifiés multicouches",
    pt: "1 a 200 µm | Laminados multicamadas"
  },
  materials: {
    en: "AISI 316L, 304, 304L",
    zh: "AISI 316L, 304, 304L",
    es: "AISI 316L, 304, 304L",
    de: "AISI 316L, 304, 304L",
    fr: "AISI 316L, 304, 304L",
    pt: "AISI 316L, 304, 304L"
  },
  link: "/SinteredWireMeshPage",
  badge: {
    en: "Rigid Diffusion Bonded",
    zh: "扩散退火烧结",
    es: "Unión por difusión",
    de: "Diffusionsgebunden",
    fr: "Frittage sous vide",
    pt: "União por difusão"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_wire_mesh_catalog.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_wire_mesh_catalog.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_wire_mesh_catalog.png-wvc-sizes",
  iconName: "layers"
}, {
  id: "wedge-wire",
  title: {
    en: "Wedge wire screens",
    zh: "楔形丝筛网",
    es: "Tamices de alambre en cuña",
    de: "Spaltsiebe",
    fr: "Crépines à fil profilé",
    pt: "Telas de arame em cunha"
  },
  description: {
    en: "Continuous slot V-wire profiles built for non-clogging solids dewatering, slurry straining, and heavy-duty radial support applications.",
    zh: "连续缝隙 V 型丝结构，专为防堵塞固液脱水、泥浆过滤和重载径向支撑应用设计。",
    es: "Perfiles de alambre en V con ranura continua para deshidratación sin colmatación, filtrado de lodos y soporte radial.",
    de: "Endlose Spaltsiebe mit V-Drahtprofilen für verstopfungsfreie Entwässerung, Schlammsiebung und radiale Stützstrukturen.",
    fr: "Profils en V à fente continue conçus pour déshydratation anti-colmatage, clarification de boues et support radial.",
    pt: "Perfis de arame em V com fenda contínua para desidratação sem entupimento, filtragem de lamas e suporte radial."
  },
  specs: {
    en: "Slot widths 0.02 mm to 5.0 mm",
    zh: "缝隙尺寸 0.02 mm 至 5.0 mm",
    es: "Anchos de ranura de 0,02 mm a 5,0 mm",
    de: "Spaltweiten 0,02 mm bis 5,0 mm",
    fr: "Largeurs de fente de 0,02 mm à 5,0 mm",
    pt: "Larguras de fenda de 0,02 mm a 5,0 mm"
  },
  materials: {
    en: "AISI 304, 316L, 321",
    zh: "AISI 304, 316L, 321",
    es: "AISI 304, 316L, 321",
    de: "AISI 304, 316L, 321",
    fr: "AISI 304, 316L, 321",
    pt: "AISI 304, 316L, 321"
  },
  link: "/WedgeWireScreenPage",
  badge: {
    en: "Non-Clogging V-Wire",
    zh: "防堵塞 V 型丝",
    es: "Alambre en V anticolmatación",
    de: "Verstopfungsfreier V-Draht",
    fr: "Fil en V anti-colmatant",
    pt: "Arame em V antientupimento"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_catalog.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_catalog.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_catalog.png-wvc-sizes",
  iconName: "filter"
}, {
  id: "filter-components",
  title: {
    en: "Filter components",
    zh: "过滤组件与滤芯",
    es: "Componentes de filtración",
    de: "Filterkomponenten",
    fr: "Composants de filtration",
    pt: "Componentes filtrantes"
  },
  description: {
    en: "Custom pleated cartridges, cylindrical filter tubes, sintered discs, and edged screen packs tailored to OEM machinery specifications.",
    zh: "定制折叠滤芯、圆柱滤筒、烧结滤盘以及包边滤片，严格适配 OEM 设备机械规格。",
    es: "Cartuchos plisados, tubos cilíndricos, discos sinterizados y paquetes enmarcados a medida para maquinaria OEM.",
    de: "Kundenspezifische Plisseepatronen, Filterzylinder, Sinterscheiben und eingefasste Siebpakete nach OEM-Vorgaben.",
    fr: "Cartouches plissées, tubes cylindriques, disques frittés et packs bordés sur mesure pour équipements OEM.",
    pt: "Cartuchos plissados, tubos cilíndricos, discos sinterizados e pacotes emoldurados sob medida para equipamentos OEM."
  },
  specs: {
    en: "Custom OD/ID & geometries | Welded or edged",
    zh: "定制外径/内径与几何构型 | 焊接或包边",
    es: "Dimensiones y geometrías personalizadas | Soldados o enmarcados",
    de: "Kundenspezifische Maße & Geometrien | Geschweißt oder gefasst",
    fr: "Dimensions et géométries sur mesure | Soudés ou bordés",
    pt: "Dimensões e geometrias sob medida | Soldados ou emoldurados"
  },
  materials: {
    en: "AISI 304, 304L, 316, 316L",
    zh: "AISI 304, 304L, 316, 316L",
    es: "AISI 304, 304L, 316, 316L",
    de: "AISI 304, 304L, 316, 316L",
    fr: "AISI 304, 304L, 316, 316L",
    pt: "AISI 304, 304L, 316, 316L"
  },
  link: "/FilterComponentsPage",
  badge: {
    en: "OEM Fabrication",
    zh: "OEM 加工制造",
    es: "Fabricación OEM",
    de: "OEM-Fertigung",
    fr: "Fabrication OEM",
    pt: "Fabricação OEM"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/filter_components_catalog.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/filter_components_catalog.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/filter_components_catalog.png-wvc-sizes",
  iconName: "cpu"
}];
const pageCopy = {
  badge: {
    en: "Product Lines & Specifications",
    zh: "产品系列与技术规格",
    es: "Líneas de producto y especificaciones",
    de: "Produktlinien & Spezifikationen",
    fr: "Gammes de produits et spécifications",
    pt: "Linhas de produtos e especificações"
  },
  title: {
    en: "Engineered Industrial Mesh & Filtration Media",
    zh: "工程级工业丝网与过滤介质",
    es: "Malla industrial de ingeniería y medios de filtración",
    de: "Technisches Industrie-Drahtgewebe & Filtermedien",
    fr: "Toiles métalliques industrielles et médias filtrants",
    pt: "Telas metálicas industriais e meios de filtração"
  },
  description: {
    en: "Explore stainless steel woven meshes, diffusion-bonded laminates, profile screens, and fabricated filtration components built to rigorous mechanical tolerances.",
    zh: "探索符合严苛机械公差的不锈钢编织网、扩散结合烧结网、楔形丝筛网以及定制加工过滤组件。",
    es: "Explore mallas tejidas de acero inoxidable, laminados sinterizados, tamices de perfil y componentes de filtración bajo tolerancias exigentes.",
    de: "Entdecken Sie Edelstahl-Drahtgewebe, diffusionsgebundene Laminate, Spaltsiebe und gefertigte Filterkomponenten nach präzisen Toleranzen.",
    fr: "Découvrez nos toiles tissées en inox, stratifiés frittés, crépines profilées et composants filtrants usinés selon des tolérances rigoureuses.",
    pt: "Explore telas tecidas em aço inoxidável, laminados sinterizados, telas de perfil e componentes filtrantes sob tolerâncias mecânicas rigorosas."
  },
  btnCharts: {
    en: "View Technical Charts",
    zh: "查看技术图表",
    es: "Ver tablas técnicas",
    de: "Technische Tabellen ansehen",
    fr: "Voir les tableaux techniques",
    pt: "Ver tabelas técnicas"
  },
  btnAllProducts: {
    en: "All Products",
    zh: "所有产品",
    es: "Todos los productos",
    de: "Alle Produkte",
    fr: "Tous les produits",
    pt: "Todos os produtos"
  },
  specRangeLabel: {
    en: "Standard range:",
    zh: "标准规格范围：",
    es: "Rango estándar:",
    de: "Standardbereich:",
    fr: "Plage standard :",
    pt: "Faixa padrão:"
  },
  specAlloyLabel: {
    en: "Available alloys:",
    zh: "可选合金材质：",
    es: "Aleaciones disponibles:",
    de: "Verfügbare Legierungen:",
    fr: "Alliages disponibles :",
    pt: "Ligas disponíveis:"
  },
  explorePrefix: {
    en: "Explore",
    zh: "探索",
    es: "Explorar",
    de: "Erkunden",
    fr: "Découvrir",
    pt: "Explorar"
  },
  supportTitle: {
    en: "Need custom mesh geometries or specific fabrication formats?",
    zh: "需要特殊网格几何尺寸或特定加工成型形式？",
    es: "¿Necesita geometrías de malla personalizadas o formatos de fabricación específicos?",
    de: "Benötigen Sie kundenspezifische Geometrien oder spezielle Konfektionierungen?",
    fr: "Besoin de géométries spécifiques ou de formats de découpe sur mesure ?",
    pt: "Precisa de geometrias de malha personalizadas ou formatos de fabricação específicos?"
  },
  supportDesc: {
    en: "We supply slit coils, cut panels, custom discs, and engineered assemblies to ASTM and ISO dimensional standards.",
    zh: "我们提供分条分切卷带、定制平片、冲压滤片及符合 ASTM 与 ISO 尺寸标准的工程组件。",
    es: "Suministramos bobinas cortadas, paneles a medida, discos y conjuntos diseñados según normas dimensionales ASTM e ISO.",
    de: "Wir liefern Spaltbänder, Zuschnitte, Ronden und Baugruppen nach ASTM- und ISO-Maßtoleranzen.",
    fr: "Nous fournissons bandes refendues, panneaux découpés, disques et assemblages conformes aux normes dimensionnelles ASTM et ISO.",
    pt: "Fornecemos bobinas cortadas, painéis sob medida, discos e conjuntos projetados conforme normas dimensionais ASTM e ISO."
  },
  btnSupport: {
    en: "Custom Mesh Engineering",
    zh: "定制丝网工程",
    es: "Ingeniería de mallas a medida",
    de: "Kundenspezifisches Engineering",
    fr: "Ingénierie sur mesure",
    pt: "Engenharia de telas sob medida"
  }
};
export default function Catalog() {
  const {
    lang
  } = useLanguage();
  const getIcon = (type: CategoryItem["iconName"]) => {
    switch (type) {
      case "grid":
        return <Grid3X3 className="size-5" aria-hidden="true" />;
      case "layers":
        return <Layers className="size-5" aria-hidden="true" />;
      case "filter":
        return <Filter className="size-5" aria-hidden="true" />;
      case "cpu":
        return <Cpu className="size-5" aria-hidden="true" />;
      default:
        return <Grid3X3 className="size-5" aria-hidden="true" />;
    }
  };
  return <section id="catalogoverview" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border/60">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-3 px-3 py-1 mf-caption uppercase tracking-wider font-semibold border-border text-foreground bg-card">
              {pick(lang, pageCopy.badge)}
            </Badge>
            <h2 className="mf-h2 text-foreground">
              {pick(lang, pageCopy.title)}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground">
              {pick(lang, pageCopy.description)}
            </p>
          </div>

          <div className="shrink-0 flex items-center gap-3">
            <Button asChild variant="outline" size="lg" className="rounded-md border-border font-medium">
              <Link to="/TechnicalChartsPage">
                {pick(lang, pageCopy.btnCharts)}
              </Link>
            </Button>
            <Button asChild variant="default" size="lg" className="rounded-md font-medium">
              <Link to="/ProductsPage">
                <span>{pick(lang, pageCopy.btnAllProducts)}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 pt-12">
          {categories.map((category, index) => {
          const categoryTitle = pick(lang, category.title);
          const categoryDesc = pick(lang, category.description);
          const categorySpecs = pick(lang, category.specs);
          const categoryMaterials = pick(lang, category.materials);
          const categoryBadge = pick(lang, category.badge);
          return <div key={category.id} data-index={index} className="h-full">
                <Reveal delay={Math.min(index, 3) * 100} className="h-full">
                  <Card className="h-full flex flex-col justify-between overflow-hidden border-border bg-card rounded-lg hover:border-primary/40 transition-[border-color] duration-200 gap-0 py-0">
                    <div>
                      <div className="mf-card-media mf-ratio-card w-full">
                        <img src={category.image} data-wvc-srcset={category.srcset} data-wvc-sizes={category.sizes} alt={categoryTitle} loading="lazy" decoding="async" className="object-cover w-full h-full" />
                        <div className="absolute top-3 left-3">
                          <Badge variant="outline" className="bg-card text-foreground font-medium mf-caption border border-border">
                            {categoryBadge}
                          </Badge>
                        </div>
                      </div>

                      <CardHeader className="pt-6 pb-2">
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                            {getIcon(category.iconName)}
                          </div>
                          <h3 className="mf-h3 text-card-foreground">
                            {categoryTitle}
                          </h3>
                        </div>
                        <p className="mf-lead text-muted-foreground mt-1">
                          {categoryDesc}
                        </p>
                      </CardHeader>

                      <CardContent className="pt-2 pb-4 space-y-2.5 mf-caption">
                        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between border-t border-border/50 pt-2.5">
                          <span className="text-muted-foreground font-medium">
                            {pick(lang, pageCopy.specRangeLabel)}
                          </span>
                          <span className="font-mono text-foreground font-medium text-right">
                            {categorySpecs}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between border-t border-border/50 pt-2">
                          <span className="text-muted-foreground font-medium">
                            {pick(lang, pageCopy.specAlloyLabel)}
                          </span>
                          <span className="font-mono text-foreground font-medium text-right">
                            {categoryMaterials}
                          </span>
                        </div>
                      </CardContent>
                    </div>

                    <CardFooter className="pt-2 pb-6 border-t border-border/40 flex items-center justify-between">
                      <Button asChild variant="ghost" size="sm" className="px-0 hover:bg-transparent text-primary hover:text-primary/80 font-medium">
                        <Link to={category.link} className="inline-flex items-center gap-1.5">
                          <span>
                            {pick(lang, pageCopy.explorePrefix)} {categoryTitle}
                          </span>
                          <ArrowRight className="size-4" aria-hidden="true" />
                        </Link>
                      </Button>
                      <Link to="/RequestAQuotePage" className="font-medium text-primary hover:underline mf-caption inline-flex items-center gap-1">
                        <span>{pick(lang, COMMON.requestQuote)}</span>
                        <ArrowRight className="size-3.5" aria-hidden="true" />
                      </Link>
                    </CardFooter>
                  </Card>
                </Reveal>
              </div>;
        })}
        </div>

        {/* Bottom Fast Selector / Assistance Ribbon */}
        <div className="mt-12 p-6 rounded-lg bg-muted/60 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-card text-primary shrink-0 hidden sm:inline-flex">
              <Cpu className="size-5" aria-hidden="true" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">
                {pick(lang, pageCopy.supportTitle)}
              </p>
              <p className="mf-caption text-muted-foreground mt-0.5">
                {pick(lang, pageCopy.supportDesc)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild variant="default" className="w-full md:w-auto font-medium">
              <Link to="/OEMCustomMeshEngineeringPage">
                {pick(lang, pageCopy.btnSupport)}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}